import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Loader2, Calendar, Users, Clock, TrendingUp, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../config/api';
import PlanningGrid from '../components/PlanningGrid';
import HealthPanel from '../components/HealthPanel';

const qp = (preview) => (preview ? `?coach_id=${preview}` : '');

export default function EspaceCoach() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const preview = user?.role === 'admin' ? params.get('preview') : null;
  const [dash, setDash] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [planning, setPlanning] = useState([]);
  const [profile, setProfile] = useState(null);
  const [hours, setHours] = useState(null);
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);

  const load = useCallback(async () => {
    try {
      const [d, s, p, pr, h, st, ev] = await Promise.all([
        axios.get(`${API}/coach/dashboard${qp(preview)}`, { withCredentials: true }),
        axios.get(`${API}/coach/my-sessions${qp(preview)}`, { withCredentials: true }),
        axios.get(`${API}/planning`),
        axios.get(`${API}/coach/profile${qp(preview)}`, { withCredentials: true }),
        axios.get(`${API}/coach/my-hours${qp(preview)}`, { withCredentials: true }),
        axios.get(`${API}/coach/my-stats${qp(preview)}`, { withCredentials: true }),
        axios.get(`${API}/coach/events${qp(preview)}`, { withCredentials: true }),
      ]);
      setDash(d.data); setSessions(s.data.sessions); setPlanning(p.data.slots);
      setProfile(pr.data); setHours(h.data); setStats(st.data); setEvents(ev.data.events);
    } catch (e) { toast.error("Impossible de charger l'espace coach."); }
  }, [preview]);
  useEffect(() => { load(); }, [load]);

  const mark = async (slot_id, date, email, status) => {
    try {
      await axios.post(`${API}/coach/attendance${qp(preview)}`, { slot_id, date, email, status }, { withCredentials: true });
      toast.success('Présence enregistrée.');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const complete = async (slot_id, date) => {
    try {
      await axios.post(`${API}/coach/session-complete${qp(preview)}`, { slot_id, date }, { withCredentials: true });
      toast.success('Séance pointée — heures déclarées.');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const cancelReq = async (slot_id, date, mode) => {
    const reason = window.prompt('Motif (facultatif) :') || '';
    try {
      await axios.post(`${API}/coach/session-cancel-request${qp(preview)}`, { slot_id, date, mode, reason }, { withCredentials: true });
      toast.success("Demande envoyée à l'administration. Les participantes ne seront jamais supprimées silencieusement.");
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const saveProfile = async (submit = false) => {
    try {
      const r = await axios.put(`${API}/coach/profile${qp(preview)}`, { ...profile, submit }, { withCredentials: true });
      toast.success(submit ? 'Profil envoyé pour validation à PURO.' : 'Profil enregistré.');
      if (r.data.status) setProfile({ ...profile, coach_status: r.data.status });
    } catch (e) { toast.error('Erreur d\'enregistrement.'); }
  };

  const respond = async (eventId, response) => {
    try {
      await axios.post(`${API}/coach/events/${eventId}/respond${qp(preview)}`, { response }, { withCredentials: true });
      toast.success('Réponse enregistrée.');
      load();
    } catch (e) { toast.error('Erreur'); }
  };

  if (!dash) return <div className="min-h-screen bg-[#F5EFE5] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#C99A55]" /></div>;

  const ATT = { presente: ['Présente', 'bg-[#EDF2EA] text-[#5C7057]'], absente: ['Absente', 'bg-[#F7E6E1] text-[#8A4A3B]'], annulation: ['Annulation', 'bg-[#FBF3DC] text-[#9A744C]'] };

  return (
    <div className="min-h-screen bg-[#F5EFE5]" data-testid="espace-coach">
      {dash.impersonated && (
        <div className="bg-[#493329] text-white text-center text-xs py-2.5 px-4" data-testid="impersonation-banner">
          <Eye className="w-3.5 h-3.5 inline mr-1.5" />
          Vous consultez actuellement l'espace de <strong>{dash.coach.name}</strong> en tant qu'administrateur. <Link to="/coach" className="underline text-[#E5C98F]">Retour au dashboard admin</Link>
        </div>
      )}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <h1 className="font-heading text-3xl text-[#493329] mb-1" data-testid="coach-greeting">Bonjour, {dash.coach.first_name}</h1>
        <p className="text-sm text-[#7A6455] mb-4">Espace Coach · <span className="text-[#C99A55]">{dash.coach.status_label}</span></p>
        {dash.pre_opening && (
          <div className="bg-[#FBF3DC] border border-[#DCC488] rounded-xl p-3 mb-5 text-xs text-[#8B6914] leading-relaxed" data-testid="coach-pre-opening-banner">
            <strong>Pré-ouverture :</strong> le studio n'est pas encore ouvert. Les créneaux ci-dessous sont des <strong>pré-réservations</strong> clientes (pré-planning) — aucune séance réelle n'est programmée tant que la date officielle d'ouverture n'est pas définie par l'administration.
          </div>
        )}

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
          {[[Calendar, 'Prochain cours', dash.cards.next_course], [Users, "Réservations aujourd'hui", dash.cards.resa_today],
            [TrendingUp, 'Taux de remplissage', `${dash.cards.fill_rate}%`],
            [Clock, 'Heures ce mois', `${dash.cards.hours_month.declared}h déclarées / ${dash.cards.hours_month.validated}h validées`]].map(([Icon, l, v], i) => (
            <div key={i} className="bg-white border border-[#E9DDCC] rounded-2xl p-4" data-testid={`coach-card-${i}`}>
              <Icon className="w-4 h-4 text-[#C99A55] mb-2" strokeWidth={1.5} />
              <p className="text-[10px] uppercase tracking-wider text-[#7A6455]">{l}</p>
              <p className="font-heading text-base text-[#493329] mt-0.5">{v}</p>
            </div>
          ))}
        </div>

        <Tabs defaultValue="sessions">
          <TabsList className="bg-white border border-[#E9DDCC] rounded-2xl p-1.5 h-auto flex-wrap w-full mb-6">
            {[['sessions', 'Mes séances'], ['planning', 'Planning'], ['sante', 'Santé'], ['heures', 'Mes heures'], ['statscoach', 'Statistiques'], ['profil', 'Mon profil'], ['events', 'Événements']].map(([v, l]) => (
              <TabsTrigger key={v} value={v} className="flex-1 min-w-[80px] rounded-xl py-2 data-[state=active]:bg-[#C99A55] data-[state=active]:text-white text-[#7A6455] text-xs" data-testid={`coach-tab-${v}`}>{l}</TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="sessions" className="space-y-3">
            {sessions.length === 0 && <p className="text-sm text-[#AE9885]">Aucun créneau ne vous est encore assigné. L'administration vous attribuera vos cours.</p>}
            {sessions.map((s) => (
              <div key={`${s.slot_id}-${s.date}`} className="bg-white border border-[#E9DDCC] rounded-2xl p-4" data-testid={`session-${s.slot_id}-${s.date}`}>
                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                  <p className="font-heading text-[#493329]">{s.day} {new Date(s.date + 'T00:00').toLocaleDateString('fr-FR')} · {s.start}–{s.end} <span className="text-xs text-[#7A6455]">{s.type.replace('Pilates Reformer – ', '')}</span></p>
                  <div className="flex gap-2">
                    {!s.completed ? (
                      <Button size="sm" onClick={() => complete(s.slot_id, s.date)} className="h-8 text-xs bg-[#9A744C] hover:bg-[#493329] text-white rounded-full" data-testid={`complete-${s.slot_id}-${s.date}`}>Séance effectuée</Button>
                    ) : <span className="text-xs text-[#5C7057]">✓ Pointée ({s.hour_status === 'validated' ? 'validée' : 'déclarée'})</span>}
                    <Button size="sm" variant="outline" onClick={() => cancelReq(s.slot_id, s.date, 'remplacement')} className="h-8 text-xs rounded-full border-[#E9DDCC] text-[#7A6455]">Demander un remplacement</Button>
                    <Button size="sm" variant="outline" onClick={() => cancelReq(s.slot_id, s.date, 'annulation')} className="h-8 text-xs rounded-full border-[#E9DDCC] text-[#C76542]">Signaler indisponibilité</Button>
                  </div>
                </div>
                {s.participants.length === 0 ? <p className="text-xs text-[#AE9885]">Aucune inscrite.</p> : s.participants.map((p) => (
                  <div key={p.email} className="flex items-center justify-between py-1.5 border-t border-[#EFE4D0]">
                    <span className="text-sm text-[#493329]">{p.name} {p.attendance && <span className={`ml-2 text-[10px] px-2 py-0.5 rounded-full ${ATT[p.attendance][1]}`}>{ATT[p.attendance][0]}</span>}</span>
                    <span className="flex gap-1">
                      {Object.keys(ATT).map((k) => (
                        <button key={k} onClick={() => mark(s.slot_id, s.date, p.email, k)}
                          className={`text-[10px] px-2 py-1 rounded-full border ${p.attendance === k ? ATT[k][1] + ' border-transparent' : 'border-[#E9DDCC] text-[#AE9885]'}`}
                          data-testid={`att-${k}-${p.email}`}>{ATT[k][0]}</button>
                      ))}
                    </span>
                  </div>
                ))}
              </div>
            ))}
          </TabsContent>

          <TabsContent value="planning">
            <p className="text-xs text-[#7A6455] mb-3">Planning global du studio (consultation). Vos modifications passent par l'administration.</p>
            <PlanningGrid slots={planning} onSelect={() => {}} testPrefix="coachview" />
          </TabsContent>

          <TabsContent value="sante">
            <HealthPanel isAdmin={false} />
          </TabsContent>

          <TabsContent value="heures">
            {hours && (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-3">
                  {[['Heures prévues', hours.summary.planned], ['Heures déclarées', hours.summary.declared], ['Heures validées PURO', hours.summary.validated]].map(([l, v]) => (
                    <div key={l} className="bg-white border border-[#E9DDCC] rounded-2xl p-4 text-center">
                      <p className="text-[10px] uppercase tracking-wider text-[#7A6455]">{l}</p>
                      <p className="font-heading text-2xl text-[#493329]">{v} h</p>
                    </div>
                  ))}
                </div>
                <div className="bg-white border border-[#E9DDCC] rounded-2xl divide-y divide-[#EFE4D0]">
                  {hours.entries.length === 0 && <p className="text-xs text-[#AE9885] p-4">Aucune heure déclarée ce mois-ci.</p>}
                  {hours.entries.map((e) => (
                    <div key={e.id} className="flex items-center justify-between px-4 py-2.5 text-sm">
                      <span className="text-[#493329]">{new Date(e.date + 'T00:00').toLocaleDateString('fr-FR')} · {e.start}–{e.end} · {e.participants} part.</span>
                      <span className={`text-xs ${e.status === 'validated' ? 'text-[#5C7057]' : e.status === 'contested' ? 'text-[#C76542]' : 'text-[#9A744C]'}`}>
                        {e.status === 'validated' ? `Validée (${(e.validated_min || e.duration_min) / 60}h)` : e.status === 'contested' ? 'Contestée' : 'Déclarée'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="statscoach">
            {stats && (
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                {[['Séances réalisées', stats.sessions_done], ['Réservations', stats.reservations], ['Participantes / séance', stats.avg_participants],
                  ['Taux de remplissage', `${stats.fill_rate}%`], ['Heures réalisées', `${stats.hours_total} h`], ['Annulations clientes', stats.cancellations]].map(([l, v]) => (
                  <div key={l} className="bg-white border border-[#E9DDCC] rounded-2xl p-4 text-center">
                    <p className="text-[10px] uppercase tracking-wider text-[#7A6455]">{l}</p>
                    <p className="font-heading text-2xl text-[#493329]">{v}</p>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="profil">
            {profile && (
              <div className="bg-white border border-[#E9DDCC] rounded-2xl p-5 space-y-3 max-w-xl" data-testid="coach-profile-form">
                {[['first_name', 'Prénom'], ['last_name', 'Nom'], ['phone', 'Téléphone'], ['siret', 'SIRET (si indépendant·e)'], ['rc_pro', 'RC professionnelle']].map(([k, l]) => (
                  <div key={k}>
                    <label className="text-[10px] uppercase tracking-wider text-[#7A6455]">{l}</label>
                    <Input value={profile[k] || ''} onChange={(e) => setProfile({ ...profile, [k]: e.target.value })} className="rounded-xl border-[#E9DDCC]" data-testid={`profile-${k}`} />
                  </div>
                ))}
                {[['bio', 'Courte présentation'], ['certifications', 'Certifications Pilates'], ['formation_reformer', 'Formation Reformer']].map(([k, l]) => (
                  <div key={k}>
                    <label className="text-[10px] uppercase tracking-wider text-[#7A6455]">{l}</label>
                    <Textarea value={profile[k] || ''} onChange={(e) => setProfile({ ...profile, [k]: e.target.value })} className="rounded-xl border-[#E9DDCC] min-h-[64px]" data-testid={`profile-${k}`} />
                  </div>
                ))}
                <div className="flex gap-2 pt-2">
                  <Button onClick={() => saveProfile(false)} variant="outline" className="rounded-xl border-[#E9DDCC]" data-testid="profile-save">Enregistrer</Button>
                  {profile.coach_status === 'profil_a_completer' && (
                    <Button onClick={() => saveProfile(true)} className="bg-[#9A744C] hover:bg-[#493329] text-white rounded-xl" data-testid="profile-submit">Envoyer pour validation</Button>
                  )}
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="events" className="space-y-3">
            {events.length === 0 && <p className="text-sm text-[#AE9885]">Aucun événement PURO à venir.</p>}
            {events.map((e) => (
              <div key={e.id} className="bg-white border border-[#E9DDCC] rounded-2xl p-4" data-testid={`event-${e.id}`}>
                <p className="font-heading text-[#493329]">{e.title} <span className="text-xs text-[#7A6455]">· {new Date(e.date + 'T00:00').toLocaleDateString('fr-FR')}</span></p>
                {e.description && <p className="text-xs text-[#7A6455] mt-1">{e.description}</p>}
                <div className="flex gap-2 mt-3">
                  {[['participe', 'Je participe'], ['ne_participe_pas', 'Je ne participe pas'], ['a_confirmer', 'À confirmer']].map(([v, l]) => (
                    <button key={v} onClick={() => respond(e.id, v)}
                      className={`text-xs px-3 py-1.5 rounded-full border ${e.my_response === v ? 'bg-[#C99A55] text-white border-transparent' : 'border-[#E9DDCC] text-[#7A6455]'}`}
                      data-testid={`event-respond-${v}`}>{l}</button>
                  ))}
                </div>
              </div>
            ))}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
